import React, { useMemo, useEffect, useState } from 'react';
import { Grid, Autocomplete, TextField, Chip, Button, Box, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';

const blueColor = '#2196f3';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    padding: theme.spacing(0.5),
    fontSize: '0.9rem',
    // backgroundColor: alpha(theme.palette.common.white, 0.9),
    height: '45px',
    overflow: 'hidden', 
  },
  '& .MuiAutocomplete-endAdornment': {
    top: 'auto',
    bottom: 'auto',
  },
  '& .MuiAutocomplete-tag': {
    margin: 2,
    maxWidth: 'calc(100% - 4px)',
  },
  '& .MuiChip-root': {
    height: 28,
    maxWidth: '100%',
    backgroundColor: blueColor,
    color: theme.palette.common.white, // Ensure text is white
    '& .MuiChip-deleteIcon': {
      color: theme.palette.common.white, // White delete icon
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.7)', // Slightly transparent on hover for visual feedback
      },
    },
    },
  '& .MuiChip-label': {
    fontSize: '0.8rem',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
    '& .MuiAutocomplete-option': {
    '&:hover': {
      backgroundColor: theme.palette.info.main // Light blue background on hover
    },
    '&[aria-selected="true"]': {
      backgroundColor: alpha(blueColor, 0.2), // Slightly darker blue for selected items
    },
    '&[aria-selected="true"].Mui-focused': {
      backgroundColor: alpha(blueColor, 0.25), // Even darker blue for selected and focused items
    },
  },
}));

const FilterBar = ({ data, filters, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState({
    investors: [],
    funds: [],
    startups: [],
    domains: [],
    fundType: [],
    ...filters
  });

  useEffect(() => {
    setLocalFilters(prevFilters => ({
      ...prevFilters,
      ...filters
    }));
  }, [filters]);

  const filterOptions = useMemo(() => {
    const getFilteredOptions = (field) => {
      return [...new Set(data
        .filter(item => 
          (localFilters.investors.length === 0 || localFilters.investors.includes(item.investor)) &&
          (localFilters.funds.length === 0 || localFilters.funds.includes(item.fundname)) &&
          (localFilters.startups.length === 0 || localFilters.startups.includes(item.startupname)) &&
          (localFilters.domains.length === 0 || localFilters.domains.includes(item.SU_Domain)) &&
          (localFilters.fundType.length === 0 || localFilters.fundType.includes(item.fundType)) 
        )
        .map(item => item[field]))
      ].filter(Boolean).sort();
    };

    return {
      investors: getFilteredOptions('investor'),
      funds: getFilteredOptions('fundname'),
      startups: getFilteredOptions('startupname'),
      domains: getFilteredOptions('SU_Domain'),
      fundType: getFilteredOptions('fundType')
    };
  }, [data, localFilters]);

  const handleFilterChange = (filterType, newValue) => {
    const updatedFilters = { ...localFilters, [filterType]: newValue };
    setLocalFilters(updatedFilters);
    onFilterChange(filterType, newValue);
  };

  const handleClearFilters = () => {
    const clearedFilters = Object.keys(localFilters).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    setLocalFilters(clearedFilters);
    Object.keys(clearedFilters).forEach(key => onFilterChange(key, []));
  };

  const renderFilter = (filterType, label) => (
    <StyledAutocomplete
      multiple
      id={`${filterType}-filter`}
      options={filterOptions[filterType]}
      value={localFilters[filterType] || []}
      onChange={(event, newValue) => handleFilterChange(filterType, newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={localFilters[filterType]?.length === 0 ? `Select ${label}` : ''}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.slice(0, 2).map((option, index) => (
          <Chip
            variant="outlined"
            label={option}
            {...getTagProps({ index })}
            key={option}
          />
        )).concat(
          value.length > 2 
            ? <Typography variant="body2" sx={{ ml: 1 }}>+{value.length - 2} more</Typography> 
            : []
        )
      }
      ListboxProps={{
        style: { 
          maxHeight: '250px',
        }
      }}
      sx={{
        '& .MuiAutocomplete-option': {
          fontSize: '1rem',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.info.main,
          },
        },
      }}
    />
  );

  return (
    <Box sx={{ 
      py: 1, 
      pb:2,
      backgroundColor: 'background.paper', 
      borderRadius: 2,
      borderColor: 'divider',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <FilterListIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography  sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Filters
        </Typography>
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          {renderFilter('funds', 'Funds')}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          {renderFilter('fundType', 'Fund Type')} {/* Changed from fundTypes to fundType */}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          {renderFilter('investors', 'Investors')}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          {renderFilter('startups', 'Startups')}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          {renderFilter('domains', 'Domains')}
        </Grid>
        <Grid item width={"14px"} xs={12} md={2}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleClearFilters}
            fullWidth
            sx={{ 
              height: '45px',
              fontSize: '1rem',
            }}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FilterBar;